import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useLocale } from "use-intl";
import { isAdminRole } from "@examora/shared/auth/roles";
import { LoadingState } from "@/components/ui/loading-state";
import { ErrorState } from "@/components/ui/error-state";
import { localizeHref, type AppLocale } from "@/i18n/routing";
import { useSession, type SessionUser } from "../hooks/use-session";

/**
 * Client-side route gates. They decide what to RENDER, not what data is
 * accessible — every protected API route enforces the same rule on the
 * server (plugins/auth.ts), so a bypassed guard only ever yields an empty,
 * 401-ing page. Mirrors the old server layouts: signed-out → /login;
 * signed-in non-admin on /admin → home.
 */
function SessionGate({
  allow,
  redirectTo,
  children,
}: {
  allow: (user: SessionUser | null) => boolean | "login";
  redirectTo: string;
  children?: ReactNode;
}) {
  const session = useSession();
  const locale = useLocale() as AppLocale;
  const location = useLocation();

  // Also wait while a signed-out answer is being re-checked (e.g. right
  // after login invalidated it) — never redirect on a stale "null".
  if (session.isPending || (session.data === null && session.isFetching)) return <LoadingState fullPage />;
  if (session.isError) return <ErrorState onRetry={() => void session.refetch()} />;

  const verdict = allow(session.data);
  if (verdict === "login") {
    return <Navigate to={localizeHref("/login", locale)} replace state={{ from: location.pathname }} />;
  }
  if (!verdict) return <Navigate to={localizeHref(redirectTo, locale)} replace />;

  return children ?? <Outlet />;
}

/** Any signed-in user. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  return (
    <SessionGate allow={(user) => (user ? true : "login")} redirectTo="/login">
      {children}
    </SessionGate>
  );
}

/** Platform staff only (same role list the backend's policy uses). */
export function RequireAdmin({ children }: { children?: ReactNode }) {
  return (
    <SessionGate allow={(user) => (!user ? "login" : isAdminRole(user.role))} redirectTo="/">
      {children}
    </SessionGate>
  );
}
